import { WALLET_TELEMETRY_ERROR_CATEGORIES } from '@/utils/wallet/telemetry-schema'
import {
	beginWalletFlow,
	classifyWalletError,
	finishWalletFlow,
	providerSlug,
	walletErrorDetails
} from '@/utils/wallet/telemetry'
import { STORAGE_WALLET_TYPE } from '@/utils/constants'
import {
	freezeComponents,
	freezeDetail,
	freezeMessage,
	installFreezeWatch,
	type FreezeInfo
} from '@/utils/freeze-watch'

const IGNORED_CODES = new Set(['418', '425'])
const reportedObjects = new WeakSet<object>()

const shouldReport = (error: any): boolean => {
	const code = String(error?.code ?? error?.response?.status ?? error?.status ?? '')
	if (IGNORED_CODES.has(code)) return false

	if (error && typeof error === 'object') {
		if (reportedObjects.has(error)) return false
		reportedObjects.add(error)
	}

	return true
}

const reportWalletOperation = (error: any): void => {
	if (!shouldReport(error)) return

	const storedProvider =
		typeof window === 'undefined'
			? 'unknown'
			: localStorage.getItem(STORAGE_WALLET_TYPE) ?? 'unknown'
	const flow = beginWalletFlow(
		'wallet_error',
		providerSlug(storedProvider),
		'wallet_core',
		'unknown'
	)
	const category = classifyWalletError(error)
	finishWalletFlow(
		flow,
		'unknown',
		category === 'user_rejected' ? 'cancelled' : 'failure',
		walletErrorDetails(error)
	)
}

const crashMessage = (error: any): string => {
	const name = typeof error?.name === 'string' ? error.name : 'Error'
	const message = typeof error?.message === 'string' ? error.message : String(error ?? '')
	return `${name}: ${message}`
		.replace(/[^A-Za-z0-9 _.,:;'()[\]/<>=+-]/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.slice(0, 120)
}

/**
 * The route a crash happened on. Segments that look like ids (addresses,
 * inscription ids, txids: anything long or hex-heavy) collapse to `:id` so
 * the path names the page, never the user or the item.
 */
export const crashPath = (pathname: unknown): string | undefined => {
	if (typeof pathname !== 'string' || !pathname.startsWith('/') || /[?#]/.test(pathname)) {
		return undefined
	}
	const collapsed = pathname
		.split('/')
		.map((segment) =>
			segment.length > 24 ||
			/^[0-9a-f]{16,}/i.test(segment) ||
			/^(bc1|[13])[a-z0-9]{20,}$/i.test(segment)
				? ':id'
				: segment
		)
		.join('/')
		.replace(/[^A-Za-z0-9_.:$\[\]/-]/g, '')
		.slice(0, 81)
	return /^\/[A-Za-z0-9_.:$\[\]/-]{0,80}$/.test(collapsed) ? collapsed : undefined
}

/**
 * The first Next.js chunk frame of a stack: `chunks/<...>.js:line:col`.
 * Names the bundle (and so the page or component) a minified error came
 * from without shipping the stack itself.
 */
export const crashFrame = (stack: unknown): string | undefined => {
	if (typeof stack !== 'string') return undefined
	const match = stack.match(
		/_next\/static\/(chunks\/[A-Za-z0-9_.$\[\]/-]{1,90}\.js):(\d{1,7}):(\d{1,7})/
	)
	if (!match) return undefined
	const frame = `${match[1]}:${match[2]}:${match[3]}`
	return /^[A-Za-z0-9_.$\[\]/-]{1,100}:\d{1,7}:\d{1,7}$/.test(frame) ? frame : undefined
}

/**
 * The React component stack, reduced to its first few frames as
 * `Name@chunks/x.js:line:col`. The error's own stack usually ends inside
 * react-dom (a "max update depth" throws from the scheduler), so this is the
 * only thing that names *our* component. Names may be minified; the chunk
 * frame still locates the module.
 */
export const crashComponents = (componentStack: unknown): string | undefined => {
	if (typeof componentStack !== 'string') return undefined
	const frames: string[] = []
	let hosts = 0
	for (const line of componentStack.split('\n')) {
		const m = line.match(
			/^\s*at\s+([A-Za-z0-9_$.]{1,40})(?:\s+\(.*?_next\/static\/(chunks\/[A-Za-z0-9_.$\[\]/-]{1,90}\.js):(\d{1,7}):(\d{1,7})\)|\s+\([^)]*\)|\s*$)/
		)
		if (!m) continue
		// Host elements (span, div, section…) carry no location and crowd out
		// the components above them; keep the innermost two as context only.
		if (!m[2] && /^[a-z]+$/.test(m[1])) {
			if (hosts < 2) frames.push(m[1])
			hosts += 1
			continue
		}
		frames.push(m[2] ? `${m[1]}@${m[2]}:${m[3]}:${m[4]}` : m[1])
		if (frames.length === 6) break
	}
	if (frames.length === 0) return undefined
	const joined = frames.join(' > ')
	return /^[A-Za-z0-9_.$\[\]/:@> -]{1,300}$/.test(joined) ? joined : undefined
}

const CRASH_ACTION_PATTERN = /^[A-Za-z0-9_.:#=@>()/[\] "'+-]{1,160}$/

let lastAction: { text: string; at: number } | null = null
let actionRecorderInstalled = false

/** `button[aria-label=Sort by] "Price"`: the element under the last click or key. */
export const describeActionTarget = (target: unknown): string | undefined => {
	const el = target as Partial<HTMLElement> | null
	if (!el || typeof el.tagName !== 'string') return undefined
	const getAttr = (name: string) =>
		typeof el.getAttribute === 'function' ? el.getAttribute(name) : null
	const parts = [el.tagName.toLowerCase()]
	const role = getAttr('role')
	if (role) parts.push(`[role=${role}]`)
	const label = getAttr('aria-label') || getAttr('name') || getAttr('placeholder')
	if (label) parts.push(`[${label.slice(0, 40)}]`)
	if (el.id) parts.push(`#${String(el.id).slice(0, 40)}`)
	const text = String(el.textContent || '')
		.replace(/\s+/g, ' ')
		.trim()
		.slice(0, 32)
	if (text) parts.push(`"${text}"`)
	const joined = parts.join('').replace(/[^A-Za-z0-9_.:#=@>()/[\] "'+-]/g, '')
	return CRASH_ACTION_PATTERN.test(joined) ? joined : undefined
}

/**
 * Remember what the user last clicked or typed into. A minified component
 * stack says "span > span > a"; this says `button[role=combobox] "Sort by"`
 * — usually enough to name the control that crashed the page.
 */
export function installCrashActionRecorder(): void {
	if (actionRecorderInstalled || typeof window === 'undefined') return
	actionRecorderInstalled = true
	const record = (event: Event) => {
		const target = event.target as Element | null
		const control =
			target && typeof target.closest === 'function'
				? target.closest('button,a,input,select,textarea,[role],[tabindex]') || target
				: target
		const text = describeActionTarget(control)
		if (text) lastAction = { text, at: Date.now() }
	}
	window.addEventListener('pointerdown', record, true)
	window.addEventListener('keydown', record, true)
}

/**
 * Whether a browser translator has rewritten the page. Google Translate
 * marks <html> with `translated-ltr`/`translated-rtl` and changes `lang`;
 * other translators change `lang` only. Either way React no longer owns
 * the text nodes it thinks it does.
 */
export const pageTranslated = (): boolean => {
	try {
		if (typeof document === 'undefined') return false
		const html = document.documentElement
		if (!html) return false
		if (/\btranslated-(ltr|rtl)\b/.test(html.className)) return true
		const lang = (html.getAttribute('lang') || '').toLowerCase()
		return (
			lang !== '' &&
			lang !== 'en' &&
			!lang.startsWith('en-') &&
			lang !== 'zh' &&
			!lang.startsWith('zh-')
		)
	} catch {
		return false
	}
}

/**
 * Keep React alive under machine translation. Translators replace text
 * nodes with <font> wrappers; when React later removes or reorders what it
 * rendered, the DOM throws NotFoundError and the tree unmounts. Ignoring
 * the two failing calls is the documented workaround
 * (facebook/react#11538); the affected text stays, the page keeps working.
 */
export function installTranslationGuard(): void {
	try {
		if (typeof Node === 'undefined' || (Node.prototype as any).__owTranslationGuard) return
		;(Node.prototype as any).__owTranslationGuard = true
		const originalRemoveChild = Node.prototype.removeChild
		Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
			if (child.parentNode !== this) return child
			return originalRemoveChild.call(this, child) as T
		}
		const originalInsertBefore = Node.prototype.insertBefore
		Node.prototype.insertBefore = function <T extends Node>(
			this: Node,
			node: T,
			reference: Node | null
		): T {
			if (reference && reference.parentNode !== this) return node
			return originalInsertBefore.call(this, node, reference) as T
		}
	} catch {}
}

export const crashAction = (): string | undefined => {
	if (!lastAction) return undefined
	const seconds = Math.max(0, Math.round((Date.now() - lastAction.at) / 1000))
	const text = `${lastAction.text} +${seconds}s`
	return CRASH_ACTION_PATTERN.test(text) ? text : undefined
}

const CHUNK_RELOAD_KEY = 'ow:chunk-reload'

/**
 * What kind of crash this is, from the error's name and message — so the
 * errors channel can tell a stale-deploy chunk from a render loop from a
 * plain TypeError without reading the frame.
 */
export const crashCategory = (error: any): (typeof WALLET_TELEMETRY_ERROR_CATEGORIES)[number] => {
	try {
		const name = String(error?.name || '')
		const message = String(error?.message || '')
		if (
			name === 'ChunkLoadError' ||
			/Loading chunk|Failed to load chunk|ChunkLoadError/i.test(message)
		)
			return 'chunk_load'
		if (/Minified React error #?185\b|Maximum update depth/i.test(message)) return 'render_loop'
		if (name === 'MainThreadFreeze') return 'long_task'
		if (/Minified React error #?(418|419|422|423|425)\b|Hydration failed|hydrat/i.test(message))
			return 'hydration'
		if (name === 'QuotaExceededError' || /quota/i.test(message)) return 'quota_exceeded'
		if (
			name === 'NotFoundError' &&
			/removeChild|insertBefore|The node to be removed|not a child of this node/i.test(message)
		)
			return 'dom_mutation'
		if (name === 'SecurityError') return 'security'
		if (/Could not load .*\.(svg|png|webp|jpe?g|gif)|texture/i.test(message)) return 'asset_load'
		if (name === 'TypeError') return 'type_error'
		if (name === 'ReferenceError') return 'reference_error'
		if (name === 'SyntaxError') return 'syntax_error'
		if (/NetworkError|Failed to fetch|Load failed/i.test(message)) return 'network'
		return 'unknown'
	} catch {
		return 'unknown'
	}
}

/**
 * A chunk that no longer exists is a tab that outlived a deploy. Reloading
 * fetches the current build; do it once per path so a genuinely broken
 * chunk cannot loop. Returns true when a reload was triggered (the caller
 * should still report, so the frequency stays visible).
 */
export function recoverFromChunkLoadError(error: any): boolean {
	try {
		if (typeof window === 'undefined') return false
		const message = String(error?.message || '')
		const isChunk =
			error?.name === 'ChunkLoadError' ||
			/Loading chunk|Failed to load chunk|ChunkLoadError/i.test(message)
		if (!isChunk) return false
		const path = window.location.pathname
		if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === path) return false
		sessionStorage.setItem(CHUNK_RELOAD_KEY, path)
		window.location.reload()
		return true
	} catch {
		return false
	}
}

/**
 * A render/runtime crash caught by an error boundary. Reported as a
 * `client_crash` telemetry event: the error name and message, the route with
 * ids collapsed, the first chunk frame and the top of the component stack
 * (no full stack, no query string, no user data), so the operator can see
 * what broke and where.
 */
/**
 * The overlays on screen when the page crashed: dialogs, alert dialogs,
 * toasts and open Radix popovers/selects, as `role#label`, newest last. A
 * render loop after "Buy now" then reads as `dialog#Buy now,status#Purchase
 * Successful` and names the surface even when the component stack is missing
 * (Next's error page gets no componentStack). Labels are cut to 24 chars
 * and the whole thing to 200; no user data lives in these labels.
 */
export function crashDom(): string | undefined {
	try {
		if (typeof document === 'undefined') return undefined
		const nodes = Array.from(
			document.querySelectorAll<HTMLElement>(
				'[role="dialog"],[role="alertdialog"],[role="status"],[role="listbox"],[role="menu"],[data-state="open"][data-radix-popper-content-wrapper],ol[data-radix-toast-viewport] li'
			)
		).slice(0, 8)
		const label = (node: HTMLElement): string => {
			const text =
				node.getAttribute('aria-label') ||
				node.querySelector('h1,h2,h3,[data-slot="title"],[role="heading"]')?.textContent ||
				node.dataset.slot ||
				node.id ||
				''
			return text
				.replace(/\s+/g, ' ')
				.trim()
				.replace(/[^A-Za-z0-9_ .:-]/g, '')
				.slice(0, 24)
		}
		const parts = nodes.map(
			(node) => `${node.getAttribute('role') || node.tagName.toLowerCase()}#${label(node)}`
		)
		if (parts.length === 0) return undefined
		return parts.join(',').slice(0, 200)
	} catch {
		return undefined
	}
}

export function reportCrash(error: any, componentStack?: string): void {
	try {
		const storedProvider =
			typeof window === 'undefined'
				? 'unknown'
				: localStorage.getItem(STORAGE_WALLET_TYPE) ?? 'unknown'
		const flow = beginWalletFlow(
			'client_crash',
			providerSlug(storedProvider),
			'error_boundary',
			'unknown'
		)
		finishWalletFlow(flow, 'unknown', 'failure', {
			error_category: crashCategory(error),
			crash_message: crashMessage(error) || 'Error',
			crash_path: typeof window === 'undefined' ? undefined : crashPath(window.location?.pathname),
			crash_frame: crashFrame(error?.stack),
			crash_components: crashComponents(componentStack),
			crash_action: crashAction(),
			...(crashDom() ? { crash_dom: crashDom() } : {}),
			...(pageTranslated() ? { page_translated: true } : {})
		})
	} catch {}
}

/**
 * A main-thread freeze, reported through the crash channel as category
 * `long_task`: the block length and DOM growth in the message, the histogram
 * of elements added during the block as the component stack, the requests
 * that preceded it as the detail. No error was thrown, so there is no frame.
 */
export function reportFreeze(info: FreezeInfo): void {
	try {
		const storedProvider =
			typeof window === 'undefined'
				? 'unknown'
				: localStorage.getItem(STORAGE_WALLET_TYPE) ?? 'unknown'
		const flow = beginWalletFlow(
			'client_crash',
			providerSlug(storedProvider),
			'error_boundary',
			'unknown'
		)
		const components = freezeComponents(info)
		const detail = freezeDetail(info)
		finishWalletFlow(flow, 'unknown', 'failure', {
			error_category: 'long_task',
			crash_message: freezeMessage(info),
			crash_path: typeof window === 'undefined' ? undefined : crashPath(window.location?.pathname),
			...(components ? { crash_components: components } : {}),
			crash_action: crashAction(),
			...(crashDom() ? { crash_dom: crashDom() } : {}),
			...(detail ? { error_detail: detail } : {}),
			...(pageTranslated() ? { page_translated: true } : {})
		})
		console.warn('Main thread freeze reported', {
			blocked_ms: info.blockedMs,
			nodes: `${info.nodesBefore}->${info.nodesAfter}`
		})
	} catch {}
}

export function installFreezeReporter(): void {
	installFreezeWatch(reportFreeze)
}

export default async function reportError(error: any, componentStack?: string): Promise<void> {
	// General React/render failures are not wallet failures: they go out as a
	// bounded crash event (name + message only) instead of the wallet funnel.
	if (!shouldReport(error)) return
	reportCrash(error, componentStack)
	console.error('Application error reported', {
		name: typeof error?.name === 'string' ? error.name.slice(0, 40) : 'Error',
		code: String(error?.code ?? error?.response?.status ?? error?.status ?? '')
			.replace(/[^A-Za-z0-9_.-]/g, '')
			.slice(0, 16)
	})
}

export async function reportWalletError(error: any): Promise<void> {
	reportWalletOperation(error)
}
